import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    Avatar,
    Chip,
    TextField,
} from '@mui/material';
import { Inventory as InventoryIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

const ProductionItemTable = ({
    items,
    actualWeights,
    onWeightChange,
    onWeightBlur,
    isReadOnly
}) => {
    const getRequestedWeight = (item) => {
        if (item.product?.unit === 'kg') {
            return parseFloat(item.quantity || 0);
        } else {
            const weightPerUnit = parseFloat(item.product?.weight_per_unit || 0);
            return parseFloat(item.quantity || 0) * weightPerUnit;
        }
    };

    const getCompletedCount = () => {
        return items.filter(item => {
            const weight = actualWeights[item.id];
            return weight && weight !== '' && !isNaN(parseFloat(weight));
        }).length;
    };

    return (
        <Paper sx={{ borderRadius: '0.75rem', border: '1px solid #d0dbe7', boxShadow: 'none', overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #d0dbe7', bgcolor: '#f6f7f8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.125rem' }}>Productos a Pesar</Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#4e7397' }}>Progreso: {getCompletedCount()}/{items.length}</Typography>
            </Box>
            <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#f6f7f8' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Producto</TableCell>
                            <TableCell align="center" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Solicitado</TableCell>
                            <TableCell sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', width: '25%' }}>Peso Final (kg)</TableCell>
                            <TableCell align="center" sx={{ color: '#4e7397', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', width: '10%' }}>Estado</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, index) => {
                            const requested = getRequestedWeight(item);
                            const actual = actualWeights[item.id] || '';
                            const isCompleted = actual !== '' && !isNaN(parseFloat(actual));

                            return (
                                <TableRow key={item.id} sx={{ bgcolor: isCompleted ? '#e8f5e9' : 'white' }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                variant="rounded"
                                                sx={{ width: 44, height: 44, bgcolor: '#f6f7f8', border: '1px solid #d0dbe7' }}
                                            >
                                                <InventoryIcon sx={{ color: '#4e7397' }} />
                                            </Avatar>
                                            <Box>
                                                <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{item.product?.name}</Typography>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>{item.product?.description}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={`${requested.toFixed(3)} kg`} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ position: 'relative' }}>
                                            <TextField
                                                type="number" size="small" fullWidth
                                                value={actual}
                                                onChange={(e) => onWeightChange(item.id, e.target.value)}
                                                onBlur={(e) => onWeightBlur(item.id, e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.target.blur();
                                                    }
                                                }}
                                                disabled={isReadOnly}
                                                inputProps={{ step: 0.001 }}
                                                sx={{
                                                    '& .MuiInputBase-input': { textAlign: 'right', fontWeight: 700, pr: 4 }
                                                }}
                                            />
                                            <Typography sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem', color: '#4e7397', pointerEvents: 'none' }}>kg</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        {isCompleted ? <CheckCircleIcon sx={{ color: '#2e7d32' }} /> : <Box sx={{ width: 20, height: 20, bgcolor: '#e0e0e0', borderRadius: '50%', mx: 'auto' }} />}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Mobile: Tarjetas de pesaje */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, p: 2 }}>
                {items.map((item) => {
                    const requested = getRequestedWeight(item);
                    const actual = actualWeights[item.id] || '';
                    const isCompleted = actual !== '' && !isNaN(parseFloat(actual));

                    return (
                        <Box key={item.id} sx={{ p: 1.5, borderRadius: '0.5rem', border: '1px solid #d0dbe7', bgcolor: isCompleted ? '#e8f5e9' : 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                <Avatar
                                    variant="rounded"
                                    sx={{ width: 40, height: 40, bgcolor: '#f6f7f8', border: '1px solid #d0dbe7', flexShrink: 0 }}
                                >
                                    <InventoryIcon sx={{ color: '#4e7397' }} />
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>{item.product?.name}</Typography>
                                    <Chip label={`Solicitado: ${requested.toFixed(3)} kg`} size="small" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                                </Box>
                                {isCompleted
                                    ? <CheckCircleIcon sx={{ color: '#2e7d32', flexShrink: 0 }} />
                                    : <Box sx={{ width: 20, height: 20, bgcolor: '#e0e0e0', borderRadius: '50%', flexShrink: 0 }} />}
                            </Box>
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    type="number" size="small" fullWidth
                                    label="Peso Final (kg)"
                                    value={actual}
                                    onChange={(e) => onWeightChange(item.id, e.target.value)}
                                    onBlur={(e) => onWeightBlur(item.id, e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
                                    disabled={isReadOnly}
                                    inputProps={{ step: 0.001 }}
                                    sx={{ '& .MuiInputBase-input': { fontWeight: 700 } }}
                                />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};

export default ProductionItemTable;
